import { useEffect, useRef, useState } from 'react';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import * as svc from '../services/prospectingService';

interface Props {
  batchId: string;
  onComplete: (status: string) => void;
}

export default function RunStatusPoller({ batchId, onComplete }: Props) {
  const [status, setStatus] = useState('RUNNING');
  const [dotCount, setDotCount] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Animate dots
    const dotTimer = setInterval(() => setDotCount(p => (p + 1) % 4), 600);

    // Poll status
    pollRef.current = setInterval(async () => {
      try {
        const data = await svc.getBatchStatus(batchId);
        setStatus(data.status);
        if (data.status !== 'RUNNING' && data.status !== 'PENDING') {
          clearInterval(pollRef.current!);
          clearInterval(dotTimer);
          onComplete(data.status);
        }
      } catch { /* continue */ }
    }, 10000);

    return () => {
      clearInterval(pollRef.current!);
      clearInterval(dotTimer);
    };
  }, [batchId, onComplete]);

  const dots = '.'.repeat(dotCount);

  if (status === 'FAILED') {
    return (
      <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
        <AlertCircle className="h-4 w-4 shrink-0" />
        Pipeline failed. Check agent logs for details.
      </div>
    );
  }

  if (status === 'REVIEW') {
    return (
      <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 text-sm">
        <CheckCircle className="h-4 w-4 shrink-0" />
        Pipeline complete — prospects ready for review.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400 text-sm">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        <span>AI agents running{dots} checking every 10s</span>
      </div>
      <div className="space-y-2 px-1">
        {['Researcher', 'Pain Analyst', 'Strategist', 'Prospect Finder', 'Outreach Drafter'].map((agent, i) => (
          <div key={agent} className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse" style={{ animationDelay: `${i * 200}ms` }} />
            <span className="text-xs text-muted-foreground">{agent}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
